import axios from "axios";
import type maplibregl from "maplibre-gl";
import React, { startTransition, useCallback, useDeferredValue, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useOverlayScrollIsolationHook } from "../../../hooks/useOverlayScrollIsolationHook";
import boatIcon from "../../../image/icons/map/transportIcons/BoatIcon.svg";
import busIcon from "../../../image/icons/map/transportIcons/BusIcon.svg";
import trainIcon from "../../../image/icons/map/transportIcons/TrainIcon.svg";
import tramIcon from "../../../image/icons/map/transportIcons/TramIcon.svg";
import { LoggerService } from "../../../services/LoggerService";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { getDelayStatusTextColor } from "../../../theme/DelayStatusColors";
import {
    getNextArrivalColorRgba,
    getTransportModeGroupColorHex,
} from "../../../tools/TransportModeColorsTools";
import {
    getTransportModeGroupFromGtfsRouteType,
    isGtfsRouteTypeTrainLike,
    TransportModeGroup,
} from "../../../tools/TransportModeGroupsTools";
import { formatStopName } from "../../../tools/stopNameFormatter";
import type { NextArrivalDto, StopArrivalsDto } from "../../../types/dtos/api/stop-platform.dto";
import type { StopWheelchairBoarding } from "../../../types/dtos/api/stop.dto";
import StopArrivalTimeMotion, {
    getArrivalCountdownDisplay,
    StopTrainDelayMotion,
} from "./StopArrivalTimeMotion";
import StopPopupArrivalRowMotion, { StopPopupArrivalListPresence } from "./StopPopupArrivalRowMotion";
import StopPopupArrivalsListMotion from "./StopPopupArrivalsListMotion";
import StopPopupBodyMotion from "./StopPopupBodyMotion";
import StopPopupMotionShell from "./StopPopupMotionShell";
import { StopWheelchairBoardingIcon } from "./StopWheelchairBoardingIcon";
import {
    STOP_POPUP_ARRIVAL_CHIP_CLASS_NAME,
    STOP_POPUP_ARRIVAL_HEADSIGN_CLASS_NAME,
    STOP_POPUP_ARRIVAL_MAIN_ROW_CLASS_NAME,
    STOP_POPUP_ARRIVAL_PER_ARRIVAL_QUAI_BADGE_CLASS_NAME,
    STOP_POPUP_ARRIVAL_PER_ARRIVAL_QUAI_PLACEHOLDER_CLASS_NAME,
    STOP_POPUP_ARRIVAL_ROUTE_BADGE_CLASS_NAME,
    STOP_POPUP_ARRIVAL_ROUTE_BADGE_MIN_WIDTH_PX,
    STOP_POPUP_ARRIVAL_SUPPLIER_ROW_CLASS_NAME,
    STOP_POPUP_ARRIVAL_TIME_PILL_CLASS_NAME,
    STOP_POPUP_ARRIVAL_TRAIN_TIME_PILL_CLASS_NAME,
    STOP_POPUP_FALLBACK_TOTAL_HEIGHT_PX,
    STOP_POPUP_HEADER_CLASS_NAME,
} from "./stopPopup.layout.constants";
import { buildStopPopupArrivalRowKey } from "./stopPopupArrivalRow.tools";
import {
    resolveStopWheelchairBoardingDisplay,
    type StopWheelchairBoardingDisplay,
} from "./stopWheelchairBoarding.tools";

interface StopPopupProps {
    "visible":   boolean;
    "lngLat":    [number, number];
    "map":       maplibregl.Map | null;
    "stopId":    string;
    "stopName"?: string;

    /** Parent station flag from {@link StopDto.isParent} at map click time. */
    "isParentStop": boolean;
    "onClose":      () => void;
    "margins"?:     StopPopupMargins;

    /** Whether dark overlay palette is active. */
    "isDarkUi": boolean;

    /**
     * Wheelchair boarding for the clicked platform stop when known.
     * Parent stations resolve per-quai values via {@link resolveWheelchairBoardingForPlatform}.
     */
    "wheelchairBoarding"?: StopWheelchairBoarding | null;

    /**
     * Looks up wheelchair boarding for a quai under a parent station
     * using preloaded child stops from the map layer controller.
     */
    "resolveWheelchairBoardingForPlatform": ( platformCode: string ) => StopWheelchairBoarding | null;
}

export interface StopPopupMargins {
    "marginTop"?:    number;
    "marginRight"?:  number;
    "marginBottom"?: number;
    "marginLeft"?:   number;
}

const POPUP_MARGIN_POINT = 20;

/**
 * Extra upward screen offset so the popup arrow targets the stop name label,
 * not the GeoJSON point (pole base). Matches MapLibre text placement in stop layers.
 */
const POPUP_ANCHOR_LABEL_OFFSET_PARENT_PX = 10;
const POPUP_ANCHOR_LABEL_OFFSET_CHILD_PX = 30;

const REFRESH_INTERVAL_MS = 15000;
const POPUP_EDGE_MARGIN_DEFAULT = 10;
const POPUP_FIXED_WIDTH = 275;
const HEADER_OPACITY = 0.75;

/** Darker brand green for default stop popup header chrome (`#0C9570`, palette `statusOnTimeText`). */
const STOP_POPUP_HEADER_FALLBACK_RGB = {
    "r": 12,
    "g": 201,
    "b": 112,
} as const;

/**
 * Default header/border rgba when no route color is available yet.
 *
 * @param opacity Alpha in `[0, 1]`.
 * @returns CSS rgba string.
 */
const getStopPopupHeaderFallbackRgba = ( opacity: number ): string => {
    const { r, g, b } = STOP_POPUP_HEADER_FALLBACK_RGB;

    return `rgba(${ r }, ${ g }, ${ b }, ${ opacity })`;
};

/**
 * Vertical header fill: green at the top, body background at the bottom.
 *
 * @param bodyBackground Stop popup body fill; transparent when omitted (light mode).
 * @returns CSS linear-gradient string.
 */
const getStopPopupHeaderBackgroundGradient = ( bodyBackground: string | undefined ): string => {
    const topColor = getStopPopupHeaderFallbackRgba( HEADER_OPACITY );
    const bottomColor = bodyBackground ?? "transparent";

    return `linear-gradient(to bottom, ${ topColor }, ${ bottomColor })`;
};

/**
 * Local visual tokens for the stop popup.
 *
 * Light values mirror the original Tailwind-based design (bg-white/60-70, border-black/5-10, gray-400/500/600/900).
 * Dark values map to the shared overlay palette. The popup-specific theme exists because the generic UI palette
 * targets opaque "card" overlays, while this popup intentionally uses lightly translucent chips over the map.
 */
interface StopPopupTheme {

    /** Optional fill behind the body; undefined keeps the body transparent over the map (light mode). */
    "bodyBackground":           string | undefined;
    "chipBackground":           string;
    "chipBorder":               string;
    "pillBackground":           string;
    "pillBorder":               string;
    "pillText":                 string;
    "perArrivalQuaiBackground": string;
    "perArrivalQuaiBorder":     string;
    "perArrivalQuaiText":       string;
    "headerText":               string;
    "primaryText":              string;
    "secondaryText":            string;
    "mutedText":                string;
    "errorText":                string;
    "clockIcon":                string;
}

const getStopPopupTheme = ( isDarkUi: boolean ): StopPopupTheme => {
    if ( isDarkUi ) {
        const palette = getUIColorPalette( true );

        return {
            "bodyBackground":           palette.overlayCardBackground,
            "chipBackground":           palette.overlayCardBackgroundSoft,
            "chipBorder":               palette.overlayBorder,
            "pillBackground":           palette.overlayCardBackgroundStrong,
            "pillBorder":               palette.overlayBorder,
            "pillText":                 palette.overlayTextPrimary,
            "perArrivalQuaiBackground": palette.overlayCardBackgroundStrong,
            "perArrivalQuaiBorder":     palette.overlayBorder,
            "perArrivalQuaiText":       palette.overlayTextSecondary,
            "headerText":               "#FFFFFF",
            "primaryText":              palette.overlayTextPrimary,
            "secondaryText":            palette.overlayTextSecondary,
            "mutedText":                palette.overlayTextSecondary,
            "errorText":                palette.statusAdvanceText,
            "clockIcon":                palette.overlayTextSecondary,
        };
    }

    return {
        "bodyBackground":           undefined,
        "chipBackground":           "rgba(255, 255, 255, 0.7)",
        "chipBorder":               "rgba(0, 0, 0, 0.05)",
        "pillBackground":           "rgba(255, 255, 255, 0.7)",
        "pillBorder":               "rgba(0, 0, 0, 0.1)",
        "pillText":                 "#111827",
        "perArrivalQuaiBackground": "rgba(255, 255, 255, 0.6)",
        "perArrivalQuaiBorder":     "rgba(0, 0, 0, 0.1)",
        "perArrivalQuaiText":       "#4B5563",
        "headerText":               "#000000",
        "primaryText":              "#111827",
        "secondaryText":            "#6B7280",
        "mutedText":                "#9CA3AF",
        "errorText":                "#DC2626",
        "clockIcon":                "#4B5563",
    };
};

interface StopArrivalClockSvgProps { "color": string }

const StopArrivalClockSvg: React.FC<StopArrivalClockSvgProps> = ( { color } ) => (
    <svg
        aria-hidden
        className="w-4 h-4 flex-shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        style={{ color }}
        viewBox="0 0 24 24"
    >
        <path
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const STOP_POPUP_HEADER_QUAI_BADGE_CLASS_NAME = "text-xs font-medium rounded bg-white/60 border border-black/10";

const STOP_POPUP_HEADER_QUAI_CONTAINER_CLASS_NAME = "inline-flex items-center gap-1 ml-2 flex-shrink-0";

interface StopQuaiLabelProps {

    /** Rider-facing platform code. */
    "platform": string;

    /** Wheelchair boarding display state, omitted when GTFS has no information. */
    "wheelchairDisplay": StopWheelchairBoardingDisplay | null;

    /** Tailwind classes for the quai and wheelchair badge shells. */
    "badgeClassName": string;

    /** Optional inline styles shared by both badge shells. */
    "badgeStyle"?: React.CSSProperties;

    /** Tailwind classes for the outer flex row (gap between badges). */
    "containerClassName"?: string;
}

/**
 * Renders a quai badge and, when known, a separate wheelchair pictogram badge to its left.
 */
const StopQuaiLabel: React.FC<StopQuaiLabelProps> = ( {
    badgeClassName,
    badgeStyle,
    containerClassName = "inline-flex items-center gap-1 flex-shrink-0",
    platform,
    wheelchairDisplay,
} ) => (
    <span className={containerClassName}>
        {wheelchairDisplay != null
            ? (
                <span
                    className={`${ badgeClassName } inline-flex items-center justify-center px-1 py-0.5`}
                    style={badgeStyle}
                >
                    <StopWheelchairBoardingIcon display={wheelchairDisplay} />
                </span>
            )
            : null}
        <span
            className={`${ badgeClassName } inline-flex items-center px-2 py-0.5`}
            style={badgeStyle}
        >
            Quai
            {" "}
            {platform}
        </span>
    </span>
);

const asStringish = ( value: unknown ): string | null => {
    if ( typeof value === "string" || typeof value === "number" ) {
        return String( value );
    }

    return null;
};


/**
 * Resolves GTFS route type to a trip icon URL.
 * The switch is exhaustive on {@link TransportModeGroup}: a new enum member without a branch is a compile-time error.
 */
const getTransportIconSrc = ( routeType: number | null ): string => {
    const group = getTransportModeGroupFromGtfsRouteType( routeType );

    switch ( group ) {
        case TransportModeGroup.TRAM:
            return tramIcon;

        case TransportModeGroup.TRAIN:
            return trainIcon;

        case TransportModeGroup.BUS:
            return busIcon;


        case TransportModeGroup.BOAT:
            return boatIcon;

        default:
            return busIcon;
    }
};


const StopPopup: React.FC<StopPopupProps> = ( {
    "isDarkUi":                           isDarkUi,
    "isParentStop":                       isParentStopProp,
    "lngLat":                             lngLat,
    "map":                                map,
    "margins":                            marginsProp,
    "onClose":                            onClose,
    resolveWheelchairBoardingForPlatform,
    "stopId":                             stopId,
    "stopName":                           stopName,
    "visible":                            visible,
    "wheelchairBoarding":                 wheelchairBoardingProp = null,
} ) => {
    const popupRef = useRef<HTMLDivElement | null>( null );
    const openedAtRef = useRef( 0 );
    const scrollRef = useRef<HTMLDivElement | null>( null );

    const [ nextArrivals, setNextArrivals ] = useState<NextArrivalDto[]>( [] );
    const [ loadingRoutes, setLoadingRoutes ] = useState<boolean>( true );
    const [ routesError, setRoutesError ] = useState<boolean>( false );
    const [ now, setNow ] = useState<number>( Date.now() );
    const [ showArrow, setShowArrow ] = useState( true );
    const [ stopPlatform, setStopPlatform ] = useState<string | null>( null );
    const [ isParentStop, setIsParentStop ] = useState<boolean>( isParentStopProp );
    const deferredArrivals = useDeferredValue( nextArrivals );
    const isArrivalsListPending = loadingRoutes || nextArrivals !== deferredArrivals;

    useEffect( () => {
        if ( !visible || stopId === "" ) {
            return;
        }

        setIsParentStop( isParentStopProp );
    }, [ visible, stopId, isParentStopProp ] );

    const marginTop = marginsProp?.marginTop ?? POPUP_EDGE_MARGIN_DEFAULT;
    const marginRight = marginsProp?.marginRight ?? POPUP_EDGE_MARGIN_DEFAULT;
    const marginBottom = marginsProp?.marginBottom ?? POPUP_EDGE_MARGIN_DEFAULT;
    const marginLeft = marginsProp?.marginLeft ?? POPUP_EDGE_MARGIN_DEFAULT;

    const formatArrival = ( value: string | null ): string => {
        if ( !value ) {
            return "--:--";
        }

        const date = new Date( value );

        if ( Number.isNaN( date.getTime() ) ) {
            return "--:--";
        }

        return date.toLocaleTimeString( "fr-FR", {
            "hour":   "2-digit",
            "minute": "2-digit",
        } );
    };

    const fetchRoutes = useCallback( async( isInitialLoad: boolean ) => {
        if ( !visible || stopId === "" ) {
            return;
        }

        if ( isInitialLoad ) {
            setLoadingRoutes( true );
            setNextArrivals( [] );
        }

        setRoutesError( false );

        try {
            const response = await axios.get<StopArrivalsDto>( `/api/stops/${ encodeURIComponent( stopId ) }/arrivals` );
            const stopArrival: StopArrivalsDto = response.data;
            const stopPlatformValue = asStringish( stopArrival.stopPlatform );

            setStopPlatform( stopPlatformValue );
            setIsParentStop( stopArrival.isParentStop );

            setNextArrivals( stopArrival.routes.filter( ( nextArrival: NextArrivalDto ) => nextArrival.routeShortName !== "" ).
                sort( ( arrivalA: NextArrivalDto, arrivalB: NextArrivalDto ) => {
                    const aTs = arrivalA.arrival != null
                        ? new Date( arrivalA.arrival ).getTime()
                        : Number.POSITIVE_INFINITY;

                    const bTs = arrivalB.arrival != null
                        ? new Date( arrivalB.arrival ).getTime()
                        : Number.POSITIVE_INFINITY;

                    return aTs - bTs;
                } ) );
            setNow( Date.now() );
        } catch( e: unknown ) {
            setNextArrivals( [] );
            setStopPlatform( null );
            setIsParentStop( isParentStopProp );
            setRoutesError( true );
            LoggerService.error( "Failed to fetch stop arrivals", "StopPopup", {
                stopId,
                "message": e instanceof Error ? e.message : String( e ),
            } );
        } finally {
            if ( isInitialLoad ) {
                startTransition( () => {
                    setLoadingRoutes( false );
                } );
            }
        }
    }, [ visible, stopId, isParentStopProp ] );

    /**
     * Positions the popup before paint and keeps the anchor stable when content height changes.
     * Uses the map-layer `isParentStop` prop for the label offset so an API refresh cannot
     * shift the anchor after the first paint.
     */
    useLayoutEffect( () => {
        if ( !visible || map === null ) {
            return;
        }

        let isCancelled = false;
        let measureFrameId: number | undefined;

        const updatePosition = () => {
            const popupElement = popupRef.current;

            if ( popupElement === null || map === null ) {
                return;
            }

            const position = map.project( lngLat );
            const labelAnchorOffsetPx = isParentStopProp
                ? POPUP_ANCHOR_LABEL_OFFSET_PARENT_PX
                : POPUP_ANCHOR_LABEL_OFFSET_CHILD_PX;

            const container = map.getContainer();
            const mapWidth = container.offsetWidth;
            const mapHeight = container.offsetHeight;
            const popupHalfWidth = POPUP_FIXED_WIDTH / 2;
            const tipY = position.y - POPUP_MARGIN_POINT - labelAnchorOffsetPx;
            const measuredHeight = popupElement.offsetHeight > 0
                ? popupElement.offsetHeight
                : STOP_POPUP_FALLBACK_TOTAL_HEIGHT_PX;

            const x = Math.max(
                popupHalfWidth + marginLeft,
                Math.min( mapWidth - popupHalfWidth - marginRight, position.x ),
            );
            const y = Math.max(
                marginTop + measuredHeight,
                Math.min( mapHeight - marginBottom, tipY ),
            );

            popupElement.style.left = `${ x - popupHalfWidth }px`;
            popupElement.style.top = `${ y - measuredHeight }px`;
            popupElement.style.visibility = "visible";

            const clampedLeft = position.x < popupHalfWidth + marginLeft;
            const clampedRight = position.x > mapWidth - popupHalfWidth - marginRight;
            const clampedTop = tipY < marginTop + measuredHeight;
            const clampedBottom = tipY > mapHeight - marginBottom;
            const isClamped = clampedLeft || clampedRight || clampedTop || clampedBottom;
            const nextShowArrow = !isClamped;

            if ( !loadingRoutes ) {
                setShowArrow( ( previousShowArrow ) => (
                    previousShowArrow === nextShowArrow ? previousShowArrow : nextShowArrow
                ) );
            }
        };

        const attachPositioning = () => {
            if ( isCancelled ) {
                return;
            }

            const popupElement = popupRef.current;

            if ( popupElement === null ) {
                window.requestAnimationFrame( attachPositioning );

                return;
            }

            popupElement.style.visibility = "hidden";
            updatePosition();
            map.on( "move", updatePosition );

            measureFrameId = window.requestAnimationFrame( () => {
                if ( !isCancelled ) {
                    updatePosition();
                }
            } );
        };

        attachPositioning();

        return () => {
            isCancelled = true;

            if ( measureFrameId !== undefined ) {
                window.cancelAnimationFrame( measureFrameId );
            }

            map.off( "move", updatePosition );
        };
    }, [
        isParentStopProp,
        lngLat,
        loadingRoutes,
        map,
        visible,
        marginTop,
        marginRight,
        marginBottom,
        marginLeft,
    ] );

    useEffect( () => {
        if ( !visible || stopId === "" ) {
            return;
        }

        void fetchRoutes( true );

        const interval = window.setInterval( () => {
            setNow( Date.now() );
            void fetchRoutes( false );
        }, REFRESH_INTERVAL_MS );

        return () => {
            window.clearInterval( interval );
        };
    }, [ visible, stopId, fetchRoutes ] );

    useEffect( () => {
        if ( visible ) {
            openedAtRef.current = performance.now();
        }
    }, [ visible ] );

    useEffect( () => {
        if ( !visible ) {
            return;
        }

        const handleClickOutside = ( event: MouseEvent | TouchEvent ) => {
            if ( event.timeStamp - openedAtRef.current < 250 ) {
                return;
            }

            const target = event.target as Node;

            if ( popupRef.current && !popupRef.current.contains( target ) ) {
                onClose();
            }
        };

        document.addEventListener( "click", handleClickOutside );

        return () => {
            document.removeEventListener( "click", handleClickOutside );
        };
    }, [ visible, onClose ] );

    useEffect( () => {
        if ( visible && scrollRef.current !== null ) {
            scrollRef.current.scrollTop = 0;
        }
    }, [ visible, stopId ] );

    useOverlayScrollIsolationHook( scrollRef, { "isEnabled": visible && !isArrivalsListPending } );

    useEffect( () => {
        if ( !visible ) {
            return;
        }

        const popupElement = popupRef.current;

        if ( popupElement === null ) {
            return;
        }

        /**
         * Block wheel propagation to the map canvas for any pointer position over the popup,
         * including row gaps and the header where the list is not scrollable.
         */
        const handleWheel = ( event: WheelEvent ): void => {
            event.stopPropagation();
        };

        popupElement.addEventListener( "wheel", handleWheel );

        return () => {
            popupElement.removeEventListener( "wheel", handleWheel );
        };
    }, [ visible ] );

    const theme = getStopPopupTheme( isDarkUi );
    const palette = getUIColorPalette( isDarkUi );
    const headerWheelchairDisplay = !isParentStop
        ? resolveStopWheelchairBoardingDisplay( wheelchairBoardingProp )
        : null;

    const resolveQuaiWheelchairDisplay = ( platform: string | null ): StopWheelchairBoardingDisplay | null => {
        if ( platform == null || platform === "" ) {
            return null;
        }

        if ( isParentStop ) {
            return resolveStopWheelchairBoardingDisplay( resolveWheelchairBoardingForPlatform( platform ) );
        }

        return resolveStopWheelchairBoardingDisplay( wheelchairBoardingProp );
    };

    return (
        <div
            className="absolute pointer-events-auto z-30"
            ref={popupRef}
            style={{ "visibility": "hidden" }}
        >
            <StopPopupMotionShell
                presenceKey={stopId}
                visible={visible}
            >
                        <div
                            className="rounded-2xl shadow-lg overflow-hidden border-2 backdrop-blur-sm"
                            style={{
                                "borderColor": getStopPopupHeaderFallbackRgba( HEADER_OPACITY ),
                                "width":       `${ POPUP_FIXED_WIDTH }px`,
                            }}
                        >
                        {/* Header */}
                        <div
                            className={STOP_POPUP_HEADER_CLASS_NAME}
                            style={{
                                "background": getStopPopupHeaderBackgroundGradient( theme.bodyBackground ),
                                "color":      theme.headerText,
                            }}
                        >
                            <span className="truncate min-w-0 flex-1">
                                {formatStopName( stopName ?? "Arrêt" )}
                            </span>
                            {!isParentStop
                                ? stopPlatform != null && stopPlatform !== ""
                                    ? (
                                        <StopQuaiLabel
                                            badgeClassName={STOP_POPUP_HEADER_QUAI_BADGE_CLASS_NAME}
                                            badgeStyle={{ "color": "#000000" }}
                                            containerClassName={STOP_POPUP_HEADER_QUAI_CONTAINER_CLASS_NAME}
                                            platform={stopPlatform}
                                            wheelchairDisplay={headerWheelchairDisplay}
                                        />
                                    )
                                    : (
                                        <span
                                            aria-hidden
                                            className={`${ STOP_POPUP_HEADER_QUAI_CONTAINER_CLASS_NAME } opacity-0 pointer-events-none`}
                                        >
                                            <StopQuaiLabel
                                                badgeClassName={STOP_POPUP_HEADER_QUAI_BADGE_CLASS_NAME}
                                                badgeStyle={{ "color": "#000000" }}
                                                platform="0"
                                                wheelchairDisplay={headerWheelchairDisplay}
                                            />
                                        </span>
                                    )
                                : null}
                        </div>
                        {/* Body */}
                        <StopPopupBodyMotion backgroundColor={theme.bodyBackground}>
                            {routesError
                                ? (
                                    <div
                                        className="w-full py-3 text-center text-sm"
                                        style={{ "color": theme.errorText }}
                                    >
                                        Impossible de récupérer les prochains passages. Veuillez réessayer plus tard.
                                    </div>
                                )
                                : !loadingRoutes && nextArrivals.length === 0
                                    ? (
                                        <div
                                            className="w-full py-3 text-center text-sm"
                                            style={{ "color": theme.secondaryText }}
                                        >
                                            Aucune ligne
                                        </div>
                                    )
                                    : (
                                        <StopPopupArrivalsListMotion
                                            isDarkUi={isDarkUi}
                                            isLoading={isArrivalsListPending}
                                            isParentStop={isParentStopProp}
                                            listRef={scrollRef}
                                        >
                                            {!isArrivalsListPending
                                                ? (
                                                    <StopPopupArrivalListPresence>
                                                        {deferredArrivals.map( ( nextArrival ) => {
                                                    const arrivalCountdown = getArrivalCountdownDisplay( nextArrival.arrival, now );
                                                    const transportModeGroup = getTransportModeGroupFromGtfsRouteType( nextArrival.routeType );
                                                    const isTrainLike = isGtfsRouteTypeTrainLike( nextArrival.routeType );
                                                    const delayMin = ( () => {
                                                        if ( nextArrival.arrival == null || nextArrival.scheduledArrival == null ) {
                                                            return null;
                                                        }

                                                        const delayMinutes = Math.round( ( new Date( nextArrival.arrival ).getTime() -
                                                          new Date( nextArrival.scheduledArrival ).getTime() ) / 60000 );

                                                        return delayMinutes > 0 ? delayMinutes : null;
                                                    } )();

                                                    return (
                                                        <StopPopupArrivalRowMotion
                                                            className={STOP_POPUP_ARRIVAL_CHIP_CLASS_NAME}
                                                            key={buildStopPopupArrivalRowKey( stopId, nextArrival )}
                                                            style={{
                                                                "backgroundColor": theme.chipBackground,
                                                                "borderColor":     theme.chipBorder,
                                                            }}
                                                        >
                                                            <div className={STOP_POPUP_ARRIVAL_MAIN_ROW_CLASS_NAME}>
                                                                <span
                                                                    aria-label="transport"
                                                                    className="inline-block h-4 w-4 flex-shrink-0"
                                                                    role="img"
                                                                    style={{
                                                                        "backgroundColor":    getTransportModeGroupColorHex( transportModeGroup ),
                                                                        "WebkitMaskImage":    `url(${ getTransportIconSrc( nextArrival.routeType ) })`,
                                                                        "WebkitMaskPosition": "center",
                                                                        "WebkitMaskRepeat":   "no-repeat",
                                                                        "WebkitMaskSize":     "contain",
                                                                        "maskImage":          `url(${ getTransportIconSrc( nextArrival.routeType ) })`,
                                                                        "maskPosition":       "center",
                                                                        "maskRepeat":         "no-repeat",
                                                                        "maskSize":           "contain",
                                                                    }}
                                                                />
                                                                <div
                                                                    className={STOP_POPUP_ARRIVAL_ROUTE_BADGE_CLASS_NAME}
                                                                    style={{
                                                                        "backgroundColor": getNextArrivalColorRgba( nextArrival ),
                                                                        "color":           "#ffffff",
                                                                        "minWidth":        `${ STOP_POPUP_ARRIVAL_ROUTE_BADGE_MIN_WIDTH_PX }px`,
                                                                    }}
                                                                >
                                                                    {nextArrival.routeShortName}
                                                                </div>
                                                                <div className="min-w-0 flex-1">
                                                                    <div
                                                                        className={STOP_POPUP_ARRIVAL_HEADSIGN_CLASS_NAME}
                                                                        style={{ "color": theme.primaryText }}
                                                                    >
                                                                        {formatStopName( nextArrival.headsign )}
                                                                    </div>
                                                                    <div className={STOP_POPUP_ARRIVAL_SUPPLIER_ROW_CLASS_NAME}>
                                                                        <div
                                                                            className="min-w-0 flex-1 truncate text-xs"
                                                                            style={{ "color": theme.secondaryText }}
                                                                        >
                                                                            {nextArrival.operator ?? "\u00a0"}
                                                                        </div>
                                                                        {isParentStop
                                                                            ? nextArrival.platform != null && nextArrival.platform !== ""
                                                                                ? (
                                                                                    <StopQuaiLabel
                                                                                        badgeClassName={STOP_POPUP_ARRIVAL_PER_ARRIVAL_QUAI_BADGE_CLASS_NAME}
                                                                                        badgeStyle={{
                                                                                            "backgroundColor": theme.perArrivalQuaiBackground,
                                                                                            "borderColor":     theme.perArrivalQuaiBorder,
                                                                                            "color":           theme.perArrivalQuaiText,
                                                                                        }}
                                                                                        platform={nextArrival.platform}
                                                                                        wheelchairDisplay={resolveQuaiWheelchairDisplay( nextArrival.platform )}
                                                                                    />
                                                                                )
                                                                                : (
                                                                                    <span
                                                                                        aria-hidden
                                                                                        className={STOP_POPUP_ARRIVAL_PER_ARRIVAL_QUAI_PLACEHOLDER_CLASS_NAME}
                                                                                    >
                                                                                        Quai 0
                                                                                    </span>
                                                                                )
                                                                            : null}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div
                                                                className="border-t"
                                                                style={{
                                                                    "borderColor": getNextArrivalColorRgba(
                                                                        nextArrival,
                                                                        HEADER_OPACITY,
                                                                    ),
                                                                }}
                                                            />
                                                            {isTrainLike
                                                                ? (
                                                                    <div
                                                                        className={STOP_POPUP_ARRIVAL_TRAIN_TIME_PILL_CLASS_NAME}
                                                                        style={{
                                                                            "backgroundColor": theme.pillBackground,
                                                                            "borderColor":     theme.pillBorder,
                                                                            "color":           theme.pillText,
                                                                        }}
                                                                    >
                                                                        <StopArrivalClockSvg color={theme.clockIcon} />
                                                                        <div className="flex items-center gap-2">
                                                                            {nextArrival.scheduledArrival != null && nextArrival.arrival != null && nextArrival.scheduledArrival !== nextArrival.arrival
                                                                                ? (
                                                                                    <span
                                                                                        className="line-through font-medium"
                                                                                        style={{ "color": theme.mutedText }}
                                                                                    >
                                                                                        {formatArrival( nextArrival.scheduledArrival )}
                                                                                    </span>
                                                                                )
                                                                                : null}
                                                                            <div className="flex items-center gap-1">
                                                                                <span
                                                                                    className="font-bold"
                                                                                    style={{
                                                                                        "color": ( () => {
                                                                                            if ( nextArrival.arrival == null || nextArrival.scheduledArrival == null ) {
                                                                                                return palette.overlayTextSecondary;
                                                                                            }
                                                                                            const delaySec = ( new Date( nextArrival.arrival ).getTime() - new Date( nextArrival.scheduledArrival ).getTime() ) / 1000;

                                                                                            return getDelayStatusTextColor( isDarkUi, delaySec );
                                                                                        } )(),
                                                                                    }}
                                                                                >
                                                                                    {formatArrival( nextArrival.arrival ?? nextArrival.scheduledArrival )}
                                                                                </span>
                                                                                <StopTrainDelayMotion
                                                                                    delayColor={palette.statusDelayText}
                                                                                    delayLabel={delayMin !== null ? `(+${ delayMin })` : null}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )
                                                                : (
                                                                    <StopArrivalTimeMotion
                                                                        arrivalCountdown={arrivalCountdown}
                                                                        clockIconColor={theme.clockIcon}
                                                                        countdownBadgeClassName={STOP_POPUP_ARRIVAL_TIME_PILL_CLASS_NAME}
                                                                        countdownBadgeStyle={{
                                                                            "backgroundColor": theme.pillBackground,
                                                                            "borderColor":     theme.pillBorder,
                                                                        }}
                                                                        formattedArrivalTime={formatArrival( nextArrival.arrival )}
                                                                        pillTextColor={theme.pillText}
                                                                    />
                                                                )}
                                                        </StopPopupArrivalRowMotion>
                                                    );
                                                } )}
                                                    </StopPopupArrivalListPresence>
                                                )
                                                : null}
                                        </StopPopupArrivalsListMotion>
                                    )}
                        </StopPopupBodyMotion>
                        </div>
                            {/* Arrow */}
                            {showArrow && (
                                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                    <div
                                        className="relative w-0 h-0 border-l-[10px] border-r-[10px] border-t-[10px] border-l-transparent border-r-transparent"
                                        style={{ "borderTopColor": getStopPopupHeaderFallbackRgba( HEADER_OPACITY ) }}
                                    />
                                </div>
                            )}
            </StopPopupMotionShell>
        </div>
    );
};

export default StopPopup;
